import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { api, resolveImg } from '../lib/api';

const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const Updates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/updates').then(({ data }) => setUpdates(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = updates;

  return (
    <div>
      <Seo title="Updates & News" description="Latest news and updates from Kassam Trading Company — new crop arrivals, shipments, certifications and export announcements." path="/updates" />
      <PageHero
        title="Latest Updates"
        subtitle="News from Kassam Trading Company: new crops, product launches, shipments and quality milestones."
        crumb="Updates"
      />

      <section className="max-w-7xl mx-auto px-6 py-16">
        {loading ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={34} /></div>
        ) : updates.length === 0 ? (
          <p className="text-center text-ktc-charcoal/60 py-20">No updates yet. Check back soon.</p>
        ) : (
          <>
            {/* Featured */}
            <Reveal>
              <Link to={`/updates/${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 items-center bg-ktc-ivoryLight rounded-2xl border border-ktc-cream overflow-hidden card-lift">
                <div className="img-zoom h-72 lg:h-full">
                  <img src={resolveImg(featured.image)} alt={featured.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 lg:pr-10">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="bg-ktc-gold text-ktc-greenDark font-semibold px-3 py-1 rounded-full">{featured.category}</span>
                    <span className="flex items-center gap-1.5 text-ktc-charcoal/55"><Calendar size={13} /> {formatDate(featured.date)}</span>
                  </div>
                  <h2 className="font-serif text-3xl font-semibold text-ktc-green mt-4 leading-snug">{featured.title}</h2>
                  <p className="text-ktc-charcoal/70 mt-3 leading-relaxed">{featured.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-ktc-gold font-semibold text-sm mt-5 group-hover:gap-2.5 transition-all">Read more <ArrowRight size={15} /></span>
                </div>
              </Link>
            </Reveal>

            {/* Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {rest.map((u, i) => (
                <Reveal key={u.id} delay={(i % 3) * 70}>
                  <Link to={`/updates/${u.slug}`} className="group block bg-ktc-ivoryLight rounded-xl border border-ktc-cream overflow-hidden card-lift h-full">
                    <div className="img-zoom h-44"><img src={resolveImg(u.image)} alt={u.title} className="w-full h-full object-cover" /></div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="bg-ktc-cream text-ktc-green font-semibold px-2.5 py-1 rounded-full">{u.category}</span>
                        <span className="flex items-center gap-1.5 text-ktc-charcoal/50"><Calendar size={12} /> {formatDate(u.date)}</span>
                      </div>
                      <h3 className="font-serif text-xl font-semibold text-ktc-green mt-3 leading-snug">{u.title}</h3>
                      <p className="text-sm text-ktc-charcoal/65 mt-2 line-clamp-2">{u.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default Updates;
